import { useEffect, useState } from 'react';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { toast, Toaster } from 'sonner';
import { Car, Search, CheckCircle2, XCircle, AlertTriangle, Gauge, Wrench, MapPin, Navigation } from 'lucide-react';

const StatusPill = ({ ok, warn, label }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-mono uppercase font-bold ${ok ? 'bg-emerald-100 text-emerald-700' : warn ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
    {ok ? <CheckCircle2 size={13}/> : warn ? <AlertTriangle size={13}/> : <XCircle size={13}/>} {label}
  </span>
);

export default function VehicleCheckPage() {
  const [enabled, setEnabled] = useState({ dvla: true, mot: true });
  const [reg, setReg] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [postcode, setPostcode] = useState('');
  const [garages, setGarages] = useState(null);
  const [gLoading, setGLoading] = useState(false);

  useEffect(() => { api.get('/vehicle/status').then((r) => setEnabled(r.data)).catch(() => {}); }, []);
  const configured = enabled.dvla || enabled.mot;

  const check = async (e) => {
    e?.preventDefault();
    if (!reg.trim()) { toast.error('Enter a registration'); return; }
    setLoading(true); setData(null);
    try {
      const r = await api.get('/vehicle/check', { params: { reg } });
      setData(r.data);
    } catch (err) {
      const s = err.response?.status;
      toast.error(s === 503 ? 'Vehicle checker is coming soon.' : s === 404 ? 'No vehicle found for that registration.' : (err.response?.data?.detail || 'Check failed'));
    } finally { setLoading(false); }
  };

  const findGarages = async (e) => {
    e?.preventDefault();
    if (!postcode.trim()) { toast.error('Enter a postcode'); return; }
    setGLoading(true); setGarages(null);
    try {
      const g = await api.get('/geo/postcode', { params: { q: postcode } });
      const r = await api.get('/vehicle/garages', { params: { lat: g.data.lat, lng: g.data.lng, radius: 5 } });
      setGarages(r.data.garages || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not find garages');
    } finally { setGLoading(false); }
  };

  const dvla = data?.dvla;
  const mot = data?.mot;
  const taxed = dvla?.tax_status === 'Taxed';
  const sorn = dvla?.tax_status === 'SORN';
  const motValid = dvla?.mot_status === 'Valid';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Free Vehicle Check — Tax, MOT & History" description="Check any UK vehicle's tax, MOT and SORN status, full MOT history with failures and mileage, plus local garages — by registration plate." />
      <Toaster position="top-center" richColors />

      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">DVLA &amp; DVSA data</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Vehicle Check</h1>
        <p className="text-slate-600 mt-2">Enter a registration to see tax, MOT &amp; SORN status, full MOT history (including what it failed on and mileage), plus local garages.</p>
      </div>

      {!configured && (
        <div className="tm-card p-5 mb-6 bg-amber-50 border-amber-200" data-testid="vehicle-disabled">
          <p className="text-sm text-amber-800"><strong>Nearly there.</strong> The vehicle checker switches on as soon as the DVLA/DVSA connection is configured. Garage search below works now.</p>
        </div>
      )}

      {/* Number plate search */}
      <form onSubmit={check} className="flex justify-center gap-2 mb-8">
        <div className="flex items-stretch rounded-lg overflow-hidden shadow-lg border border-black max-w-md w-full">
          <div className="bg-[#0b3d91] text-white flex items-center px-2 font-display font-semibold text-sm">GB</div>
          <input value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())} placeholder="ENTER REG"
            data-testid="vehicle-reg-input"
            className="flex-1 bg-[#ffdd00] text-black font-display font-semibold text-2xl sm:text-3xl uppercase tracking-widest text-center px-3 py-3 outline-none placeholder:text-black/40"
            style={{ fontFamily: "'UKNumberPlate', system-ui, sans-serif" }} />
        </div>
        <button type="submit" className="tm-btn" data-testid="vehicle-check-btn"><Search size={16}/> Check</button>
      </form>

      {loading && <p className="font-mono text-slate-400 text-center" data-testid="vehicle-loading">Checking…</p>}

      {!loading && data && (
        <div className="space-y-6" data-testid="vehicle-results">
          {/* Summary */}
          <div className="tm-card p-5">
            <div className="flex items-center gap-3 mb-4">
              <Car size={24} className="text-brand"/>
              <h2 className="font-display font-semibold text-2xl uppercase">{dvla?.make || mot?.make || 'Vehicle'} {mot?.model || ''}</h2>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {dvla && <StatusPill ok={taxed} warn={sorn} label={sorn ? 'SORN' : dvla.tax_status || 'Tax unknown'} />}
              {dvla && <StatusPill ok={motValid} label={dvla.mot_status || 'MOT unknown'} />}
            </div>
            {dvla && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                {[['Colour', dvla.colour], ['Fuel', dvla.fuel], ['Year', dvla.year], ['Engine', dvla.engine_cc && `${dvla.engine_cc}cc`],
                  ['CO₂', dvla.co2 != null && `${dvla.co2} g/km`], ['Tax due', dvla.tax_due], ['MOT expires', dvla.mot_expiry], ['First reg', dvla.first_registered]]
                  .filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}><p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{k}</p><p className="font-bold">{v}</p></div>
                  ))}
              </div>
            )}
          </div>

          {/* MOT history */}
          {mot?.tests?.length > 0 && (
            <div className="tm-card p-5" data-testid="vehicle-mot-history">
              <h3 className="font-display font-bold text-lg uppercase mb-4 flex items-center gap-2"><Gauge size={18} className="text-brand"/> MOT history</h3>
              <div className="space-y-3">
                {mot.tests.map((t, i) => (
                  <div key={i} className="border-l-2 pl-4 py-1" style={{ borderColor: t.result === 'PASSED' ? '#10b981' : '#f43f5e' }} data-testid={`mot-test-${i}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="font-bold">{t.date ? new Date(t.date).toLocaleDateString('en-GB') : '—'}</span>
                      <StatusPill ok={t.result === 'PASSED'} label={t.result || '—'} />
                    </div>
                    {t.odometer != null && <p className="text-sm text-slate-500">{Number(t.odometer).toLocaleString('en-GB')} {t.odometer_unit === 'mi' ? 'miles' : (t.odometer_unit || 'miles')}{t.expiry ? ` · expired/expires ${new Date(t.expiry).toLocaleDateString('en-GB')}` : ''}</p>}
                    {t.defects?.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {t.defects.map((d, j) => (
                          <li key={j} className={`text-xs flex items-start gap-1 ${d.dangerous ? 'text-rose-600 font-bold' : d.type === 'ADVISORY' || d.type === 'MINOR' ? 'text-amber-600' : 'text-slate-600'}`}>
                            <span className="uppercase font-semibold">[{d.dangerous ? 'DANGEROUS' : d.type}]</span> {d.text}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-3">Mileage readings come from each MOT — a drop or stall can indicate a clock issue. Data from DVSA.</p>
            </div>
          )}
        </div>
      )}

      {/* Garages near you */}
      <div className="tm-card p-5 mt-8">
        <h3 className="font-display font-bold text-lg uppercase mb-3 flex items-center gap-2"><Wrench size={18} className="text-brand"/> Garages near you</h3>
        <form onSubmit={findGarages} className="flex gap-2">
          <div className="relative flex-1">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="tm-input pl-9" placeholder="Postcode (e.g. M1 1AE)" value={postcode} onChange={(e) => setPostcode(e.target.value)} data-testid="garage-postcode-input" />
          </div>
          <button type="submit" className="tm-btn" data-testid="garage-search-btn"><Search size={16}/> Find</button>
        </form>
        {gLoading && <p className="font-mono text-sm text-slate-400 mt-3">Finding garages…</p>}
        {!gLoading && garages && (
          garages.length === 0 ? <p className="text-sm text-slate-400 mt-3">No garages found nearby. Try a wider area.</p> : (
            <div className="space-y-2 mt-4" data-testid="garage-results">
              {garages.map((g, i) => (
                <div key={i} className="flex items-center gap-3 p-2 border-b border-slate-100" data-testid={`garage-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{g.name}</p>
                    {g.address && <p className="text-xs text-slate-500 truncate">{g.address}</p>}
                    {g.phone && <p className="text-xs text-slate-400">{g.phone}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-xs font-bold text-slate-700">{g.distance} mi</div>
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${g.lat},${g.lng}`} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-brand hover:underline flex items-center gap-0.5 justify-end"><Navigation size={11}/> Directions</a>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
        <p className="text-[11px] text-slate-400 mt-3">Garage locations from OpenStreetMap. Live garage prices aren't publicly available — please call ahead for a quote.</p>
      </div>
    </div>
  );
}
